/**
 * Keep the reader's place in the transcript across side-stage layout swaps.
 *
 * Opening or closing the scripture/video stage changes the layout around the
 * transcript: at >=1024px with a stage open, `.gw-chat-pane` scrolls and
 * `.gw-body` is `overflow: hidden`; in the floating panel and on phones the
 * stage covers or stacks above the transcript inside `.gw-body`. Without an
 * anchor, closing the Bible reader dumped the reader at the top of the
 * conversation instead of the turn they were reading.
 *
 * Capture where the reader is before the swap, then scroll back there once
 * the new layout has painted. We anchor on an element (not a pixel offset)
 * because the chat column changes width with the stage, so offsets don't
 * survive the reflow. A reader pinned to the end of the transcript is
 * restored to the end, not to the top of the latest turn.
 *
 * While a stage hides the transcript, nothing in it is visible, so capture
 * returns null; the caller restores the anchor it saved when the stage
 * opened instead (see `WidgetApp`'s stage handlers).
 */

const TURN_SELECTOR = ".gw-turn";
// Slack for "the reader is at the end": the sentinel sits just below the
// last turn, under the transcript's bottom padding.
const AT_END_TOLERANCE_PX = 32;

export type ScrollAnchor = {
  element: HTMLElement;
  /** The transcript's end sentinel was in view: restore to the end. */
  atEnd?: boolean;
  end?: HTMLElement | null;
  /**
   * The turn's top relative to the transcript box's top when captured
   * (negative once the reader has scrolled into the turn). Restoring it
   * keeps a reader who was deep inside a long answer at that spot instead
   * of jumping back to the turn's first line.
   */
  offset?: number;
};

type Bounds = { top: number; bottom: number };

function viewportBounds(root: ParentNode): Bounds {
  const rect = (root as Partial<HTMLElement>).getBoundingClientRect?.();
  if (rect && rect.bottom > rect.top) return { top: rect.top, bottom: rect.bottom };
  const height = typeof window === "undefined" ? Number.POSITIVE_INFINITY : window.innerHeight;
  return { top: 0, bottom: height };
}

/**
 * The first transcript turn visible inside ``root``'s box, or the last turn
 * when the reader has scrolled past every turn. Null when there are no turns
 * or every turn is below the visible area (a stage is covering the
 * transcript), so a caller never anchors on a turn the reader wasn't seeing.
 */
export function captureScrollAnchor(
  root: ParentNode | null | undefined,
  end?: HTMLElement | null,
): ScrollAnchor | null {
  if (!root) return null;
  const turns = Array.from(root.querySelectorAll<HTMLElement>(TURN_SELECTOR));
  if (turns.length === 0) return null;
  const bounds = viewportBounds(root);
  const rects = turns.map((turn) => turn.getBoundingClientRect());
  const visibleIdx = rects.findIndex((r) => r.bottom > bounds.top && r.top < bounds.bottom);
  const lastIdx = turns.length - 1;
  const allAbove = rects[lastIdx].bottom <= bounds.top;
  const idx = visibleIdx >= 0 ? visibleIdx : allAbove ? lastIdx : -1;
  if (idx < 0) return null;
  const endRect = end?.getBoundingClientRect();
  const atEnd =
    !!endRect && endRect.top <= bounds.bottom + AT_END_TOLERANCE_PX && endRect.bottom >= bounds.top;
  const offset = visibleIdx >= 0 ? rects[idx].top - bounds.top : 0;
  return { element: turns[idx], atEnd, end: end ?? null, offset };
}

/**
 * Scroll back to the anchor in whichever ancestor now owns scroll: the end
 * sentinel for a reader who was at the end, otherwise the anchored turn's
 * top. Runs after two animation frames so React has committed the stage
 * change and the browser has laid it out.
 */
export function restoreScrollAnchor(
  anchor: ScrollAnchor | null,
  schedule: (cb: () => void) => void = defaultSchedule,
): void {
  if (!anchor) return;
  schedule(() => {
    if (anchor.atEnd && anchor.end?.isConnected) {
      anchor.end.scrollIntoView({ block: "end", behavior: "auto" });
      return;
    }
    if (!anchor.element.isConnected) return;
    anchor.element.scrollIntoView({ block: "start", behavior: "auto" });
    const scroller = anchor.offset ? scrollParent(anchor.element) : null;
    if (scroller) scroller.scrollTop -= anchor.offset ?? 0;
  });
}

/** Nearest ancestor that actually scrolls vertically (the transcript's
 * scroll owner changes with the stage layout, so it's found, not passed). */
function scrollParent(element: HTMLElement): HTMLElement | null {
  if (typeof getComputedStyle !== "function") return null;
  for (let node = element.parentElement; node; node = node.parentElement) {
    const overflowY = getComputedStyle(node).overflowY;
    if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) {
      return node;
    }
  }
  return null;
}

function defaultSchedule(cb: () => void): void {
  if (typeof requestAnimationFrame !== "function") {
    cb();
    return;
  }
  requestAnimationFrame(() => requestAnimationFrame(cb));
}

export type StagePlaceKeeper = {
  /** Open (or swap) a stage. ``stageWasOpen``: a stage was already showing. */
  open(stageWasOpen: boolean, change: () => void): void;
  /** Close a stage. ``stageStaysOpen``: another stage is still showing. */
  close(stageStaysOpen: boolean, change: () => void): void;
};

/**
 * Remembers the reader's place when the first stage opens and returns them
 * there when the last stage closes. The place is captured at open time
 * because by close time the stage covers (phone overlay) or sits above
 * (floating panel) the transcript, so a fresh capture would find no turn the
 * reader was actually looking at.
 */
export function createStagePlaceKeeper(
  getRoot: () => ParentNode | null | undefined,
  getEnd: () => HTMLElement | null | undefined,
  restore: (anchor: ScrollAnchor | null) => void = restoreScrollAnchor,
): StagePlaceKeeper {
  let saved: ScrollAnchor | null = null;
  const capture = () => captureScrollAnchor(getRoot(), getEnd());
  return {
    open(stageWasOpen, change) {
      const anchor = capture();
      if (!stageWasOpen) saved = anchor;
      change();
      restore(anchor);
    },
    close(stageStaysOpen, change) {
      if (stageStaysOpen) {
        const anchor = capture();
        change();
        restore(anchor);
        return;
      }
      const anchor = saved ?? capture();
      saved = null;
      change();
      restore(anchor);
    },
  };
}
